import React, { useState } from 'react';
import axios from 'axios';
import { Zap, CheckCircle, AlertCircle, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GeothermalContact = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    project_phase: '',
    location: '',
    well_depth: '',
    expected_temperature: '',
    h2s_expected: '',
    resource_assessment: '',
    permit_needs: '',
    project_scale: '',
    timeline: '',
    specific_requirements: '',
    additional_info: '',
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        subject: `Geothermal Project Inquiry - ${formData.project_phase}`,
        message: `
Company: ${formData.company}
Phone: ${formData.phone}
Project Phase: ${formData.project_phase}
Location: ${formData.location}
Well Depth: ${formData.well_depth}
Expected Temperature: ${formData.expected_temperature}
H2S Expected: ${formData.h2s_expected}
Resource Assessment: ${formData.resource_assessment}
Permit Needs: ${formData.permit_needs}
Project Scale: ${formData.project_scale}
Timeline: ${formData.timeline}
Specific Requirements or Concerns: ${formData.specific_requirements}
Additional Information: ${formData.additional_info}
        `.trim(),
        newsletter_signup: false,
      };

      const response = await axios.post(`${API}/contact`, payload);
      
      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: 'Thank you! We will review your geothermal project details and contact you within 1-2 business days.',
        });
        setFormData({
          first_name: '',
          last_name: '',
          company: '',
          email: '',
          phone: '',
          project_phase: '',
          location: '',
          well_depth: '',
          expected_temperature: '',
          h2s_expected: '',
          resource_assessment: '',
          permit_needs: '',
          project_scale: '',
          timeline: '',
          specific_requirements: '',
          additional_info: '',
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'There was an error submitting your inquiry. Please try again or call us directly at (907) 687-8263.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-900 to-purple-700 px-8 py-12 text-white">
            <div className="flex items-center mb-4">
              <Zap className="w-12 h-12 mr-4" />
              <div>
                <h1 className="text-3xl font-bold">Geothermal Project Inquiry</h1>
                <p className="text-purple-100 mt-2">Tell us about your geothermal exploration or development project</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            {status.message && (
              <div className={`mb-6 p-4 rounded-lg flex items-start space-x-3 ${status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {status.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <p>{status.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                    <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name <span className="text-red-500">*</span></label>
                    <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company/Organization <span className="text-red-500">*</span></label>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone <span className="text-red-500">*</span></label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Project Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Phase</label>
                    <select name="project_phase" value={formData.project_phase} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                      <option value="">Select project phase</option>
                      <option value="exploration">Initial Exploration</option>
                      <option value="test_drilling">Test Well Drilling</option>
                      <option value="resource_confirmation">Resource Confirmation</option>
                      <option value="development">Development Planning</option>
                      <option value="production">Production Phase</option>
                      <option value="feasibility">Feasibility Study</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location/Community</label>
                    <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="e.g., Pilgrim Hot Springs, Akutan" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expected Well Depth</label>
                    <input type="text" name="well_depth" value={formData.well_depth} onChange={handleChange} placeholder="e.g., 2000 feet, 1000 meters" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expected Temperature</label>
                    <select name="expected_temperature" value={formData.expected_temperature} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                      <option value="">Select temperature range</option>
                      <option value="low">Low (Under 150°C / 300°F)</option>
                      <option value="moderate">Moderate (150-200°C / 300-390°F)</option>
                      <option value="high">High (Over 200°C / 390°F)</option>
                      <option value="unknown">Unknown/To Be Determined</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">H2S Expected?</label>
                    <select name="h2s_expected" value={formData.h2s_expected} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                      <option value="">Select expectation</option>
                      <option value="yes_high">Yes - High Concentration Expected</option>
                      <option value="yes_moderate">Yes - Moderate Concentration</option>
                      <option value="yes_low">Yes - Low Concentration</option>
                      <option value="unknown">Unknown</option>
                      <option value="no">No H2S Expected</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Resource Assessment Status</label>
                    <select name="resource_assessment" value={formData.resource_assessment} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                      <option value="">Select status</option>
                      <option value="preliminary">Preliminary Assessment Only</option>
                      <option value="geophysical">Geophysical Survey Completed</option>
                      <option value="temperature_gradient">Temperature Gradient Holes Drilled</option>
                      <option value="slim_hole">Slim Hole Testing Completed</option>
                      <option value="production_well">Production Well Testing</option>
                      <option value="none">No Assessment Yet</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permit Needs</label>
                    <select name="permit_needs" value={formData.permit_needs} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                      <option value="">Select permit needs</option>
                      <option value="air_quality">Air Quality Permits</option>
                      <option value="drilling">Drilling Permits</option>
                      <option value="testing">Testing Phase Permits</option>
                      <option value="h2s_monitoring">H2S Monitoring Plan</option>
                      <option value="comprehensive">Comprehensive Permitting Package</option>
                      <option value="consultation">Initial Consultation</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Scale</label>
                    <select name="project_scale" value={formData.project_scale} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                      <option value="">Select project scale</option>
                      <option value="direct_use">Direct Use (Heating)</option>
                      <option value="small_power">Small Power Generation (Under 5 MW)</option>
                      <option value="medium_power">Medium Power Generation (5-20 MW)</option>
                      <option value="large_power">Large Power Generation (Over 20 MW)</option>
                      <option value="district_heating">District Heating System</option>
                      <option value="combined">Combined Heat & Power</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
                    <select name="timeline" value={formData.timeline} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                      <option value="">Select timeline</option>
                      <option value="immediate">Immediate (Drilling Season Approaching)</option>
                      <option value="3_months">Within 3 months</option>
                      <option value="6_months">6 months</option>
                      <option value="1_year">1 year</option>
                      <option value="planning">Long-term Planning (1+ years)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specific Requirements or Concerns <span className="text-red-500">*</span></label>
                    <textarea name="specific_requirements" value={formData.specific_requirements} onChange={handleChange} required rows="4" placeholder="e.g., H2S abatement needs, baseline monitoring, community concerns, etc." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                    <textarea name="additional_info" value={formData.additional_info} onChange={handleChange} rows="3" placeholder="Any other details about your geothermal project..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"></textarea>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-purple-700 text-white py-4 px-6 rounded-lg font-semibold hover:bg-purple-800 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? <span>Submitting...</span> : <><span>Submit Geothermal Inquiry</span><Send size={20} /></>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeothermalContact;
